import Image from 'next/future/image';

import cn from 'classnames';
import { CartLine } from '@lambda/commons/apis/shopify/types/storefront';
import { useEffect, useState } from 'react';
import { handleFreeReturn } from '@lambda/commons/helpers';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import hand from '@/public/icons/cart/hand.svg';
import warrantyB from '@/public/icons/cart/warrantyB.svg';
import guaranteeB from '@/public/icons/cart/guaranteeB.svg';
import freeShipping from '@/public/icons/cart/freeShipping.svg';
import cartReebeloLogo from '@/public/icons/cart/cartReebeloLogo.svg';

import {
  generateCartWarrantyText,
  generateCategoriesConditions,
} from '@/lib/cart';

const t = i18n.init();

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

export default function CartStaticInfo({
  lineItems,
}: {
  lineItems: CartLine[];
}) {
  const liCss = 'flex items-center py-1 pb-2 text-sm text-gray-700';
  const imageContainerCss =
    'relative mr-4 inline-flex items-center justify-center text-center';
  const [warrantyText, setWarrantyText] = useState('');
  const { conditions, categoryPks } = generateCategoriesConditions(lineItems);

  useEffect(() => {
    const displayWarrantyText = async () => {
      const _warrantyText = await generateCartWarrantyText({
        conditions,
        categoryPks,
        freeReturnDays,
      });

      setWarrantyText(_warrantyText);
    };

    displayWarrantyText();
  }, [lineItems]);

  return (
    <>
      <p className="flex w-full items-center py-3 px-5 text-sm font-bold sm:px-7 sm:py-4">
        <span className="relative -left-1 mr-2 inline-flex h-[28px] w-[28px] items-center justify-center text-center">
          <Image alt="hand" src={hand} fill />
        </span>
        {t`Buy with Peace of Mind`}
      </p>
      <ul className="w-full border-t border-gray-500 px-4 py-3 sm:px-7 sm:py-5">
        <li className={liCss}>
          <span className={cn('h-[20px] w-[18px]', imageContainerCss)}>
            <Image alt="guaranteeB" src={guaranteeB} fill />
          </span>
          {t`${{
            free_return_days: freeReturnDays,
          }} Days Money Back Guarantee`}
        </li>
        <li className={liCss}>
          <span className={cn('h-[20px] w-[18px]', imageContainerCss)}>
            <Image alt="freeShipping" src={freeShipping} fill />
          </span>
          {t`Free Shipping & Returns`}
        </li>
        {!!warrantyText && (
          <li className={liCss}>
            <span className={cn('h-[20px] w-[18px]', imageContainerCss)}>
              <Image alt="warrantyB" src={warrantyB} fill />
            </span>
            {`${warrantyText}`}
          </li>
        )}
        <li className={liCss}>
          <span className={cn('h-[18px] w-[18px]', imageContainerCss)}>
            <Image alt="cartReebeloLogo" src={cartReebeloLogo} fill />
          </span>
          {t`Guaranteed quality. No extra fee`}
        </li>
      </ul>
    </>
  );
}
